/**
 * Bumper Renderer — neon vertical lines at world edges that react to
 * tank proximity and flash on wall bounce (pinball-style).
 * Edge darkening beyond the lines frames the arena on wider screens.
 */

import { Container, Graphics } from 'pixi.js';
import { WORLD, BUMPER, Z } from '../constants';
import type { Player } from '../types';
import type { WallBounceResult } from '../physics/tanks';

/** Visible world-space extent — extends beyond VIRTUAL_WIDTH on wide screens. */
function visibleX(): { off: number; w: number } {
  const w = WORLD.CANVAS_WIDTH / WORLD.SCALE;
  return { off: (WORLD.VIRTUAL_WIDTH - w) / 2, w };
}

/** Lerp between two 0xRRGGBB colors by t (0..1). */
function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

interface SideState {
  flashAlpha: number;
  flashWidthPulse: number;
  flashColor: number;
  proximity: number; // 0..1
}

export class BumperRenderer {
  private container: Container;
  private darkenGfx: Graphics;
  private bumperGfx: Graphics;
  private left: SideState;
  private right: SideState;
  private dirty = true;
  private prevLeftAlpha = -1;
  private prevRightAlpha = -1;

  constructor(worldContainer: Container) {
    this.container = new Container();
    this.container.zIndex = Z.BUMPERS;
    worldContainer.addChild(this.container);

    this.darkenGfx = new Graphics();
    this.container.addChild(this.darkenGfx);

    this.bumperGfx = new Graphics();
    this.container.addChild(this.bumperGfx);

    this.left = { flashAlpha: 0, flashWidthPulse: 0, flashColor: BUMPER.LINE_BRIGHT, proximity: 0 };
    this.right = { flashAlpha: 0, flashWidthPulse: 0, flashColor: BUMPER.LINE_BRIGHT, proximity: 0 };

    this.drawEdgeDarkening();
  }

  /** Draw static edge darkening strips from world boundaries outward. */
  private drawEdgeDarkening(): void {
    const { off, w } = visibleX();
    const leftEdge = (WORLD.VIRTUAL_WIDTH - w) / 2;
    const rightEdge = leftEdge + w;
    const worldLeft = WORLD.WORLD_LEFT;
    const worldRight = WORLD.WORLD_RIGHT;

    // Only draw if visible area extends beyond world boundaries
    const g = this.darkenGfx;
    g.clear();

    const strips = 5;

    // Left darkening: from leftEdge to worldLeft
    if (leftEdge < worldLeft) {
      const span = worldLeft - leftEdge;
      for (let i = 0; i < strips; i++) {
        const t0 = i / strips;
        const t1 = (i + 1) / strips;
        const x0 = leftEdge + span * t0;
        const x1 = leftEdge + span * t1;
        // Alpha decreases toward world boundary (darkest at visible edge)
        const alpha = BUMPER.EDGE_ALPHA * (1 - (t0 + t1) / 2);
        g.rect(x0, 0, x1 - x0, WORLD.VIRTUAL_HEIGHT);
        g.fill({ color: BUMPER.EDGE_COLOR, alpha });
      }
    }

    // Right darkening: from worldRight to rightEdge
    if (rightEdge > worldRight) {
      const span = rightEdge - worldRight;
      for (let i = 0; i < strips; i++) {
        const t0 = i / strips;
        const t1 = (i + 1) / strips;
        const x0 = worldRight + span * t0;
        const x1 = worldRight + span * t1;
        // Alpha increases away from world boundary (darkest at visible edge)
        const alpha = BUMPER.EDGE_ALPHA * (t0 + t1) / 2;
        g.rect(x0, 0, x1 - x0, WORLD.VIRTUAL_HEIGHT);
        g.fill({ color: BUMPER.EDGE_COLOR, alpha });
      }
    }
  }

  /** Called from physics on wall bounce. */
  triggerFlash(result: WallBounceResult): void {
    const side = result.side === 'left' ? this.left : this.right;
    const color = result.isSpike ? BUMPER.LINE_SPIKE : BUMPER.LINE_BRIGHT;
    // Use Math.max — timer conflict rule
    side.flashAlpha = Math.max(side.flashAlpha, BUMPER.FLASH_ALPHA * result.intensity);
    side.flashWidthPulse = Math.max(side.flashWidthPulse, BUMPER.WIDTH_PULSE * result.intensity);
    side.flashColor = color;
    this.dirty = true;
  }

  /** Update proximity glow from ragdoll players near edges. */
  updateProximity(players: Player[]): void {
    let leftProx = 0;
    let rightProx = 0;

    for (const p of players) {
      if (!p.alive) continue;
      if (BUMPER.TUMBLE_ONLY && !p.tumble) continue;

      const distLeft = p.x - WORLD.WORLD_LEFT;
      const distRight = WORLD.WORLD_RIGHT - p.x;

      if (distLeft < BUMPER.PROXIMITY_RANGE) {
        leftProx = Math.max(leftProx, 1 - distLeft / BUMPER.PROXIMITY_RANGE);
      }
      if (distRight < BUMPER.PROXIMITY_RANGE) {
        rightProx = Math.max(rightProx, 1 - distRight / BUMPER.PROXIMITY_RANGE);
      }
    }

    if (this.left.proximity !== leftProx) {
      this.left.proximity = leftProx;
      this.dirty = true;
    }
    if (this.right.proximity !== rightProx) {
      this.right.proximity = rightProx;
      this.dirty = true;
    }
  }

  /** Decay flash and redraw if dirty. */
  update(dt: number): void {
    // Exponential decay of flash
    if (this.left.flashAlpha > 0) {
      this.left.flashAlpha *= Math.exp(-BUMPER.FLASH_DECAY * dt);
      if (this.left.flashAlpha < 0.005) this.left.flashAlpha = 0;
      this.dirty = true;
    }
    if (this.left.flashWidthPulse > 0) {
      this.left.flashWidthPulse *= Math.exp(-BUMPER.WIDTH_DECAY * dt);
      if (this.left.flashWidthPulse < 0.01) this.left.flashWidthPulse = 0;
      this.dirty = true;
    }
    if (this.right.flashAlpha > 0) {
      this.right.flashAlpha *= Math.exp(-BUMPER.FLASH_DECAY * dt);
      if (this.right.flashAlpha < 0.005) this.right.flashAlpha = 0;
      this.dirty = true;
    }
    if (this.right.flashWidthPulse > 0) {
      this.right.flashWidthPulse *= Math.exp(-BUMPER.WIDTH_DECAY * dt);
      if (this.right.flashWidthPulse < 0.01) this.right.flashWidthPulse = 0;
      this.dirty = true;
    }

    // Check if effective alpha changed enough to warrant redraw
    const leftAlpha = this.effectiveAlpha(this.left);
    const rightAlpha = this.effectiveAlpha(this.right);

    if (!this.dirty) {
      if (Math.abs(leftAlpha - this.prevLeftAlpha) > 0.005 ||
          Math.abs(rightAlpha - this.prevRightAlpha) > 0.005) {
        this.dirty = true;
      }
    }

    if (!this.dirty) return;
    this.dirty = false;
    this.prevLeftAlpha = leftAlpha;
    this.prevRightAlpha = rightAlpha;

    const g = this.bumperGfx;
    g.clear();

    this.drawBumperLine(g, WORLD.WORLD_LEFT, this.left);
    this.drawBumperLine(g, WORLD.WORLD_RIGHT, this.right);
  }

  /** Compute effective alpha from idle + proximity + flash. */
  private effectiveAlpha(side: SideState): number {
    const proxAlpha = side.proximity * BUMPER.MAX_ALPHA;
    return Math.min(1, BUMPER.IDLE_ALPHA + proxAlpha + side.flashAlpha);
  }

  /** Draw a three-layer Sakurai bumper line (dark backing, main neon, highlight core). */
  private drawBumperLine(g: Graphics, x: number, side: SideState): void {
    const effective = this.effectiveAlpha(side);
    const pulse = side.flashWidthPulse;
    const flashT = side.flashAlpha; // 0..1 blend toward flash color

    // Layer 1: Dark backing
    const darkAlpha = Math.min(0.6, effective * 2);
    g.moveTo(x, 0);
    g.lineTo(x, WORLD.VIRTUAL_HEIGHT);
    g.stroke({ color: BUMPER.DARK_BACKING, width: BUMPER.DARK_WIDTH + pulse, alpha: darkAlpha });

    // Layer 2: Main neon — lerp between idle and flash color
    const mainColor = lerpColor(BUMPER.LINE_COLOR, side.flashColor, Math.min(1, flashT));
    g.moveTo(x, 0);
    g.lineTo(x, WORLD.VIRTUAL_HEIGHT);
    g.stroke({ color: mainColor, width: BUMPER.MAIN_WIDTH + pulse * 0.5, alpha: effective });

    // Layer 3: Highlight core — Sakurai: flashy enough to clearly communicate
    const hlAlpha = Math.min(1, BUMPER.IDLE_ALPHA * 0.5 + flashT * 0.8 + side.proximity * 0.2);
    g.moveTo(x, 0);
    g.lineTo(x, WORLD.VIRTUAL_HEIGHT);
    g.stroke({ color: BUMPER.HIGHLIGHT, width: BUMPER.HIGHLIGHT_WIDTH, alpha: hlAlpha });
  }

  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }
}
